/* cf-gst-inclusive.js - Click Fencing, 2026-08-20
 *
 * WHAT THIS IS FOR
 * ----------------
 * The store moved to GST-INCLUSIVE display pricing: every Shopify price raised 10% and
 * "Include sales tax in product price and shipping rate" switched on, so the number on the
 * product page is the number the customer pays and Shopify adds nothing at checkout.
 *
 * Three of the four calculators need no change for that. They sum Shopify prices and emit
 * no tax line, so once those prices include GST their quote total is simply the payable
 * amount. This file fixes the two places that do NOT follow automatically.
 *
 * ------------------------------------------------------------------
 * 1. GLASS ADDS ITS OWN GST - which against inclusive prices is double
 * ------------------------------------------------------------------
 * ullc-calc.js -> updateQuoteDisplay() ends with:
 *
 *     const gstAmount = total * .1;
 *     total += gstAmount;
 *     this.state.quoteBreakdown.push({category:"Tax", item:"GST (10%)", ...});
 *
 * Correct against ex-GST prices. Against GST-inclusive prices it charges GST twice - a
 * verified $104.78 on a $1,047.77 job. This strips that line and takes the total back down
 * to the sum of the real lines. Dropping it is also the end state agreed on 2026-08-12:
 * all four calculators then speak one currency and the display logic has one path.
 *
 * ------------------------------------------------------
 * 2. THE QUOTED DELIVERY CHARGE IS STILL THE ex-GST $90
 * ------------------------------------------------------
 * All four templates render `"deliveryCharge": 90` into #shopify-calculator-data, and the
 * calculators read it as `this.shopifyData.settings?.deliveryCharge ?? 90`.
 *
 * Pete confirmed 2026-08-20 that the $90 metro rate is $90 PLUS GST, so the Shopify
 * shipping rate is now $99 inclusive. But cf-real-line-items.js deliberately keeps the
 * delivery line OUT of the cart and lets Shopify apply the rate once (see
 * click-fencing-DELIVERY-DOUBLE-CHARGE-2026-08-18.md). So the quote would SHOW $90 while
 * checkout CHARGED $99 - a $9 overcharge on every delivered order, which is exactly the
 * failure the reconciliation exists to prevent.
 *
 * Patching the shared settings object is the cheap fix: the alternative is editing four
 * page templates totalling ~428KB. cf-calc-parts-fix.liquid is the precedent - all four
 * calculators read window.glassCalculator.shopifyData.
 *
 * GUARDED: only rewrites the value when it is exactly the known ex-GST 90. If the
 * templates are ever corrected to 99 this becomes a no-op rather than making it $108.90.
 *
 * SAFE TO PUBLISH BEFORE OR AFTER THE SWITCH
 * ------------------------------------------
 * Both behaviours are keyed off window.CF_TAXES_INCLUDED, which meta-tags.liquid renders
 * straight from {{ shop.taxes_included }}. While the store is ex-GST this file is a no-op.
 *
 * Self-installing, idempotent, every path in try/catch so a pricing display fault can
 * never break the calculator. Same shape as cf-calc-fixes.js / cf-gst-display.js.
 */
(function () {
  'use strict';

  if (window.__cfGstInclusiveInstalled) return;
  window.__cfGstInclusiveInstalled = true;

  if (window.CF_TAXES_INCLUDED !== true) {
    console.log('[cf-gst-inclusive] store prices are ex-GST - no-op, glass keeps its own GST line');
    return;
  }

  var DELIVERY_EX_GST = 90;    // what the four templates hard-code
  var DELIVERY_INC_GST = 99;   // the Shopify metro rate, GST inclusive

  function isTaxLine(b) {
    if (!b) return false;
    var cat = String(b.category == null ? '' : b.category).trim();
    var item = String(b.item == null ? '' : b.item);
    return /^tax$/i.test(cat) || /\bgst\b/i.test(item);
  }

  /* Idempotent by construction: with no tax line present it returns having done nothing,
     so a repaint that happens to re-enter cannot loop or compound. Verified in-page that
     updateQuoteBreakdown() does NOT call back into updateQuoteDisplay(). */
  function stripTax(gc) {
    var st = gc && gc.state;
    if (!st) return;
    var bd = st.quoteBreakdown;
    if (!bd || !bd.length) return;

    var tax = 0, kept = [];
    for (var i = 0; i < bd.length; i++) {
      if (isTaxLine(bd[i])) tax += Number(bd[i].totalPrice) || 0;
      else kept.push(bd[i]);
    }
    if (!(tax > 0)) return;

    st.quoteBreakdown = kept;
    if (typeof st.quote === 'number') {
      st.quote = Math.round((st.quote - tax) * 100) / 100;
    }

    try { if (typeof gc.updateQuoteElements === 'function') gc.updateQuoteElements(); } catch (e) {}
    try { if (typeof gc.updateQuoteBreakdown === 'function') gc.updateQuoteBreakdown(); } catch (e) {}
  }

  /* Only ever 90 -> 99, so running twice, or on a theme whose templates have been
     corrected, does nothing. */
  function fixDelivery(gc) {
    var s = gc && gc.shopifyData && gc.shopifyData.settings;
    if (!s) return false;
    if (Number(s.deliveryCharge) !== DELIVERY_EX_GST) return false;
    s.deliveryCharge = DELIVERY_INC_GST;
    console.log('[cf-gst-inclusive] delivery charge ' + DELIVERY_EX_GST + ' -> ' + DELIVERY_INC_GST + ' (GST inclusive)');
    return true;
  }

  var tries = 0;

  function install() {
    if (tries++ > 60) return;
    try {
      var gc = window.glassCalculator || window.glassFencingCalculator;
      if (!gc) { setTimeout(install, 500); return; }

      fixDelivery(gc);

      var proto = Object.getPrototypeOf(gc);
      if (!proto || typeof proto.updateQuoteDisplay !== 'function') { setTimeout(install, 500); return; }
      if (proto.__cfGstInclusivePatched) return;
      proto.__cfGstInclusivePatched = true;

      var orig = proto.updateQuoteDisplay;
      proto.updateQuoteDisplay = function () {
        /* Re-assert before the quote is built, in case the calculator re-read its data. */
        try { fixDelivery(this); } catch (e) {}
        var r = orig.apply(this, arguments);
        try { stripTax(this); } catch (e) { console.warn('[cf-gst-inclusive] strip failed', e); }
        return r;
      };

      /* A quote may already be on screen when this lands. */
      try { stripTax(gc); } catch (e) {}

      console.log('[cf-gst-inclusive] installed - glass GST line suppressed, prices include GST');
    } catch (e) {
      console.warn('[cf-gst-inclusive] install failed', e);
      setTimeout(install, 800);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install);
  else install();
  setTimeout(install, 1500);
  setTimeout(install, 4000);
})();
